import { Button, Dialog, Field } from '@noorden-insurtech/alba-design-system';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FilterCombobox } from '@/features/reports/components/shared/filter-combobox.component';
import type {
  PolicyBatchBulkResult,
  PolicyBatchFacets,
  PolicyBatchItem,
  PolicyBatchPatch,
} from '@/services/integrations';
import { POLICY_BATCH_STATUS_OPTIONS } from '@/shared/enums/policy-batch-status.enum';
import {
  buildBatchIdOptions,
  buildChannelCodeOptions,
  buildPolicyBatchKey,
  formatLobOption,
} from '../utils/policy-batch-options.util';

interface PolicyBatchBulkEditModalProps {
  isOpen: boolean;
  /** Itens marcados na tabela — pré-carregam a lista, mas dá para tirar/adicionar aqui. */
  selected: PolicyBatchItem[];
  /** Itens da página atual, usados no combobox de adicionar. */
  available: PolicyBatchItem[];
  facets?: PolicyBatchFacets;
  result?: PolicyBatchBulkResult | null;
  /** Pré-checagem no i4Pro rodando — dispara ao salvar com status Disponível. */
  isChecking?: boolean;
  onClose: () => void;
  onSelectionChange: (items: PolicyBatchItem[]) => void;
  onRequestSave: (patch: PolicyBatchPatch) => void;
}

/** `''` = "não mexer neste campo" — só campo preenchido entra no patch. */
const KEEP = '';

export function PolicyBatchBulkEditModal({
  isOpen,
  selected,
  available,
  facets,
  result,
  isChecking = false,
  onClose,
  onSelectionChange,
  onRequestSave,
}: PolicyBatchBulkEditModalProps) {
  const [status, setStatus] = useState(KEEP);
  const [channelCode, setChannelCode] = useState(KEEP);
  const [batchId, setBatchId] = useState(KEEP);

  useEffect(() => {
    if (isOpen) {
      setStatus(KEEP);
      setChannelCode(KEEP);
      setBatchId(KEEP);
    }
  }, [isOpen]);

  const channelCodeOptions = useMemo(
    () => buildChannelCodeOptions(facets),
    [facets]
  );
  const batchIdOptions = useMemo(() => buildBatchIdOptions(facets), [facets]);

  const selectedKeys = useMemo(
    () => new Set(selected.map(buildPolicyBatchKey)),
    [selected]
  );

  const addOptions = useMemo(
    () =>
      available
        .filter(item => !selectedKeys.has(buildPolicyBatchKey(item)))
        .map(item => ({
          value: buildPolicyBatchKey(item),
          label: `${item.policyNumber} — ramo ${item.lobId}`,
        })),
    [available, selectedKeys]
  );

  const patch: PolicyBatchPatch = {
    ...(status ? { status: status as PolicyBatchPatch['status'] } : {}),
    ...(channelCode ? { channelCode: Number(channelCode) } : {}),
    ...(batchId ? { batchId: Number(batchId) } : {}),
  };

  const hasPatch = Object.keys(patch).length > 0;
  const canSave = hasPatch && selected.length > 0 && !result && !isChecking;

  const removeItem = (key: string) =>
    onSelectionChange(
      selected.filter(item => buildPolicyBatchKey(item) !== key)
    );

  const addItem = (key: string) => {
    const found = available.find(item => buildPolicyBatchKey(item) === key);
    if (found) onSelectionChange([...selected, found]);
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={details => {
        if (!details.open) onClose();
      }}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[640px] max-w-[92vw]'>
          <Dialog.Header>
            <Dialog.Title>Edição em lote</Dialog.Title>
          </Dialog.Header>

          <Dialog.Body className='max-h-[65vh] space-y-4 overflow-y-auto'>
            {result ? (
              <BulkResult result={result} />
            ) : (
              <>
                <Field.Root>
                  <Field.Label>
                    Apólices selecionadas ({selected.length})
                  </Field.Label>
                  <div className='flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded-lg border border-alba-gray-200 p-3'>
                    {selected.length === 0 && (
                      <span className='text-sm text-alba-gray-500'>
                        Nenhuma apólice selecionada.
                      </span>
                    )}
                    {selected.map(item => {
                      const key = buildPolicyBatchKey(item);
                      return (
                        <span
                          key={key}
                          className='flex items-center gap-1 rounded-full bg-alba-gray-200 px-3 py-1 text-xs text-alba-gray-800'
                        >
                          {item.policyNumber}
                          <button
                            type='button'
                            aria-label={`Remover ${item.policyNumber}`}
                            onClick={() => removeItem(key)}
                            className='rounded-full p-0.5 hover:bg-alba-gray-300'
                          >
                            <X className='h-3 w-3' />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </Field.Root>

                <Field.Root>
                  <Field.Label>Adicionar apólice</Field.Label>
                  <FilterCombobox
                    placeholder='Buscar na página atual'
                    options={addOptions}
                    value=''
                    onChange={value => value && addItem(value)}
                  />
                </Field.Root>

                <div className='space-y-4 border-t border-alba-gray-200 pt-4'>
                  <p className='text-sm font-semibold text-alba-black-500'>
                    Aplicar a todas as selecionadas
                  </p>
                  <p className='text-xs text-alba-gray-500'>
                    Campo deixado em branco não é alterado.
                  </p>

                  <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                    <Field.Root>
                      <Field.Label>Status</Field.Label>
                      <FilterCombobox
                        placeholder='— manter —'
                        options={POLICY_BATCH_STATUS_OPTIONS}
                        value={status}
                        onChange={setStatus}
                      />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>Origem Emissão</Field.Label>
                      <FilterCombobox
                        placeholder='— manter —'
                        options={channelCodeOptions}
                        value={channelCode}
                        onChange={setChannelCode}
                      />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>Nº Balde</Field.Label>
                      <FilterCombobox
                        placeholder='— manter —'
                        options={batchIdOptions}
                        value={batchId}
                        onChange={setBatchId}
                      />
                    </Field.Root>
                  </div>

                  {status && (
                    <p className='text-xs text-alba-gray-600'>
                      Os números passarão para{' '}
                      <strong>{statusLabel(status)}</strong>. Liberar um número
                      consulta o i4Pro antes de gravar — os que já estiverem em
                      uso lá são recusados individualmente.
                    </p>
                  )}
                </div>
              </>
            )}
          </Dialog.Body>

          <Dialog.Footer className='flex justify-end gap-2'>
            <Button variant='ghost' size='md' onClick={onClose}>
              {result ? 'Fechar' : 'Cancelar'}
            </Button>
            {!result && (
              <Button
                variant='primary'
                size='md'
                onClick={() => onRequestSave(patch)}
                disabled={!canSave}
                loading={isChecking}
                loadingText='Consultando i4Pro...'
              >
                Salvar {selected.length > 0 ? `(${selected.length})` : ''}
              </Button>
            )}
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

function statusLabel(value: string): string {
  return (
    POLICY_BATCH_STATUS_OPTIONS.find(option => option.value === value)?.label ??
    value
  );
}

/** Resultado agregado — no molde do modal de importação. */
function BulkResult({ result }: { result: PolicyBatchBulkResult }) {
  const errors = result.errors ?? [];

  return (
    <div className='space-y-3'>
      <div className='flex gap-4 text-sm'>
        <span className='text-alba-gray-700'>
          Atualizadas: <strong>{result.updated}</strong>
        </span>
        <span
          className={
            errors.length ? 'text-alba-error-red-600' : 'text-alba-gray-700'
          }
        >
          Com erro: <strong>{result.failed}</strong>
        </span>
      </div>

      {errors.length > 0 && (
        <div className='max-h-64 space-y-2 overflow-y-auto rounded-lg border border-alba-gray-200 p-3'>
          {errors.slice(0, 20).map(error => (
            <p
              key={`${error.lobId}-${error.policyNumber}`}
              className='text-xs text-alba-gray-700'
            >
              <span className='font-medium'>{error.policyNumber}</span> (
              {formatLobOption(error.lobId)}): {error.message}
            </p>
          ))}
          {errors.length > 20 && (
            <p className='text-xs text-alba-gray-500'>
              ... e mais {errors.length - 20} erro(s).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
